import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ShoppingBag,
  Phone,
  Trash2,
  Plus,
  Minus,
  X,
} from "lucide-react";
import { useCart, formatEUR } from "@/lib/cartStore";
import { RESTAURANT } from "@/data/catalog";

/**
 * "Récapitulatif de commande" — Sheet global persisté en localStorage.
 *
 * - Bouton flottant visible dès qu'un article est ajouté.
 * - Mobile : sheet qui glisse du bas. Desktop : sheet à droite.
 * - Pas de paiement : c'est une checklist pour l'appel téléphonique.
 */
export const RecapDrawer = () => {
  const { items, count, total, removeItem, updateQty, clear, drawerOpen, openDrawer, closeDrawer } = useCart();

  if (count === 0) return null;

  return (
    <Sheet open={drawerOpen} onOpenChange={(o) => (o ? openDrawer() : closeDrawer())}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="recap-fab"
          aria-label={`Voir le récapitulatif (${count} article${count > 1 ? "s" : ""})`}
          className="fixed z-40 bottom-4 left-4 md:bottom-6 md:left-6 inline-flex items-center gap-2.5 rounded-full bg-[#FF9500] px-4 py-3 text-sm font-street tracking-wider text-black shadow-[0_6px_20px_rgba(255,149,0,0.45),0_4px_0_#7a4900] active:translate-y-0.5 transition-all"
        >
          <span className="relative inline-flex">
            <ShoppingBag size={17} strokeWidth={2.6} />
            <span className="absolute -top-2 -right-2 inline-flex h-4.5 min-w-[1.1rem] items-center justify-center rounded-full bg-black text-white text-[10px] font-bold px-1 leading-none">
              {count}
            </span>
          </span>
          <span className="hidden sm:inline">Récap</span>
          <span className="font-display text-base leading-none">
            {formatEUR(total)}
          </span>
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="bg-[#0F0F0F] border-l border-[#262626] text-white w-full sm:max-w-md p-0 flex flex-col"
        data-testid="recap-drawer"
      >
        <SheetHeader className="p-5 border-b border-[#262626] text-left">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF9500] font-street">
            Récapitulatif de commande
          </div>
          <SheetTitle className="font-street text-2xl tracking-wide leading-none mt-1 text-white">
            Votre sélection
          </SheetTitle>
          <SheetDescription className="text-[11px] italic text-white/55 leading-snug">
            Prix estimatif. Commande à confirmer par téléphone.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {items.map((line) => (
            <article
              key={line.id}
              data-testid={`recap-line-${line.id}`}
              className="rounded-xl border border-[#262626] bg-[#0A0A0A] p-3.5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-street text-sm tracking-wide uppercase text-white leading-snug">
                    {line.name}
                  </div>
                  {line.details && line.details.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {line.details.map((d, i) => (
                        <li
                          key={i}
                          className="text-[11.5px] text-white/65 leading-snug"
                        >
                          {d}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(line.id)}
                  data-testid={`recap-remove-${line.id}`}
                  aria-label="Retirer"
                  className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#262626] text-white/55 hover:text-[#DC2626] hover:border-[#DC2626]/40 transition-colors"
                >
                  <X size={13} />
                </button>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div className="inline-flex items-center rounded-md border border-[#262626] bg-[#121212]">
                  <button
                    type="button"
                    onClick={() => updateQty(line.id, line.qty - 1)}
                    data-testid={`recap-qty-minus-${line.id}`}
                    aria-label="Diminuer la quantité"
                    disabled={line.qty <= 1}
                    className="inline-flex h-7 w-7 items-center justify-center text-white/75 hover:text-[#FF9500] disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Minus size={12} />
                  </button>
                  <span
                    className="w-6 text-center font-display text-sm text-white"
                    data-testid={`recap-qty-${line.id}`}
                  >
                    {line.qty}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateQty(line.id, line.qty + 1)}
                    data-testid={`recap-qty-plus-${line.id}`}
                    aria-label="Augmenter la quantité"
                    className="inline-flex h-7 w-7 items-center justify-center text-white/75 hover:text-[#FF9500]"
                  >
                    <Plus size={12} />
                  </button>
                </div>
                <div className="font-display text-lg leading-none text-[#FF9500]">
                  {formatEUR(line.unitPrice * line.qty)}
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="border-t border-[#262626] p-5 space-y-3 bg-[#0F0F0F]">
          <div
            data-testid="recap-total"
            className="rounded-xl bg-[#FF9500] text-black p-4 flex items-center justify-between"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold">
                Total estimé
              </div>
              <div className="font-display text-3xl leading-none mt-1">
                {formatEUR(total)}
              </div>
            </div>
            <div className="text-right text-[10px] uppercase tracking-[0.22em] font-bold leading-tight">
              {count} article{count > 1 ? "s" : ""}
            </div>
          </div>

          <a
            href={RESTAURANT.phoneTel}
            data-testid="recap-call-cta"
            className="w-full font-street inline-flex items-center justify-center gap-2 rounded-md bg-[#FF9500] px-5 py-3.5 text-base tracking-wider text-black shadow-[0_5px_0_#7a4900] hover:-translate-y-0.5 hover:shadow-[0_7px_0_#7a4900] transition-all"
          >
            <Phone size={17} strokeWidth={2.6} />
            Appeler pour commander
          </a>
          <div className="text-center text-[11px] text-white/55">
            {RESTAURANT.phoneDisplay}
          </div>

          <button
            type="button"
            onClick={() => {
              if (window.confirm("Vider le récapitulatif ?")) clear();
            }}
            data-testid="recap-clear"
            className="w-full font-street inline-flex items-center justify-center gap-1.5 rounded-md border border-[#262626] px-4 py-2 text-[11px] tracking-wider text-white/55 hover:text-[#DC2626] hover:border-[#DC2626]/40 transition-colors"
          >
            <Trash2 size={12} />
            Vider le récapitulatif
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default RecapDrawer;
